import NextLink, { LinkProps as NextLinkProps } from "next/link";
import {
  AnchorHTMLAttributes,
  FC,
  MouseEvent,
  useCallback,
} from "react";
import { isExternalUrl } from "utils/is-external-url";

export type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> &
  NextLinkProps;

/**
 * A universal Link component that:
 * - Automatically decides between Next.js <Link> and <a>
 * - Supports iframe-safe click handling (via postMessage)
 * - Preserves all anchor props while extending NextLink props
 */
export const Link: FC<LinkProps> = ({
  children,
  href,
  as,
  replace,
  scroll,
  shallow,
  passHref,
  prefetch,
  locale,
  legacyBehavior,
  onClick,
  ...anchorProps
}) => {
  /**
   * Props passed to Next.js <Link>
   */
  const nextLinkProps: NextLinkProps = {
    href,
    as,
    replace,
    scroll,
    shallow,
    passHref,
    prefetch,
    locale,
    legacyBehavior,
  };

  /**
   * Handles click events for both internal and external links.
   *
   * Behavior:
   * - If inside an iframe → block navigation and notify parent window
   * - Otherwise → pass event to user-supplied onClick handler
   */
  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      // Detect iframe usage and send message to parent instead of navigating
      if (window.self !== window.top && href) {
        e.preventDefault();
        e.stopPropagation();

        window?.parent?.postMessage(
          {
            source: "theme-content",
            topic: "redirect",
            href,
          },
          "*"
        );
      }

      // Forward event if consumer passed custom onClick
      if (onClick) {
        onClick(e);
      }
    },
    [href, onClick]
  );

  /**
   * Renders:
   * - <NextLink><a /></NextLink> for internal links
   * - <a> for external links
   * - <span> when no href is provided
   */
  return (
    <>
      {href && !isExternalUrl(href) ? (
        // Internal Next.js link
        <NextLink
          {...nextLinkProps}
          href={
            typeof href === "string"
              ? href.replace(/^\/products\//gi, "/")
              : href
          }
        >
          <a onClick={handleClick} {...anchorProps}>
            {children}
          </a>
        </NextLink>
      ) : href ? (
        // External <a> link
        <a
          href={
            typeof href === "string"
              ? href.replace(/^\/products\//gi, "/")
              : href
          }
          rel={
            anchorProps?.target === "_blank"
              ? "noopener noreferrer"
              : undefined
          }
          onClick={handleClick}
          {...anchorProps}
        >
          {children}
        </a>
      ) : (
        // No href → behave like text button
        <span onClick={onClick} {...anchorProps}>
          {children}
        </span>
      )}
    </>
  );
};
